import { ChatMessage as ChatMessageType, ChatMessageMetadata, FullContentData } from '../types/chat';

export class ChatMessage implements ChatMessageType {
  id: string;
  sessionId: string;
  userId?: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: string;
  metadata?: ChatMessageMetadata;
  status?: 'sending' | 'sent' | 'delivered' | 'error';
  fullContent?: FullContentData;
  
  // New properties for improved message state management
  public isStreaming?: boolean;
  public isPlaceholder?: boolean;
  public tempId?: string; // For optimistic updates

  constructor(data: Partial<ChatMessageType> = {}) {
    this.id = data.id || this.generateId();
    this.sessionId = data.sessionId || '';
    this.userId = data.userId;
    this.content = data.content || '';
    this.role = data.role || 'user';
    this.timestamp = data.timestamp || new Date().toISOString();
    this.metadata = data.metadata;
    this.status = data.status || 'sent';
    this.fullContent = data.fullContent;
    
    // Initialize new properties
    this.isStreaming = data.isStreaming || false;
    this.isPlaceholder = data.isPlaceholder || false;
    this.tempId = data.tempId;
  }

  private generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if this is a user message
   */
  isUserMessage(): boolean {
    return this.role === 'user';
  }

  /**
   * Check if this is an assistant message
   */
  isAssistantMessage(): boolean {
    return this.role === 'assistant';
  }

  /**
   * Check if this is a system message
   */
  isSystemMessage(): boolean {
    return this.role === 'system';
  }

  /**
   * Check if this message is currently streaming
   */
  isCurrentlyStreaming(): boolean {
    return this.isStreaming || this.status === 'sending';
  }

  /**
   * Get formatted timestamp
   */
  getFormattedTime(): string {
    return new Date(this.timestamp).toLocaleTimeString();
  }

  /**
   * Get the display content - prefer fullContent.text.value over content
   */
  getDisplayContent(): string {
    // Handle both object and array formats from backend
    if (this.fullContent) {
      // If fullContent is an array, use the first element
      const contentData = Array.isArray(this.fullContent) ? this.fullContent[0] : this.fullContent;
      if (contentData?.text?.value) {
        return contentData.text.value;
      }
    }
    return this.content;
  }

  /**
   * Get annotations for rich content display
   */
  getAnnotations() {
    if (this.fullContent) {
      // Handle both object and array formats from backend
      const contentData = Array.isArray(this.fullContent) ? this.fullContent[0] : this.fullContent;
      return contentData?.text?.annotations || [];
    }
    return [];
  }

  /**
   * Check if message has annotations
   */
  hasAnnotations(): boolean {
    return this.getAnnotations().length > 0;
  }

  /**
   * Get unique citations from annotations, ordered by their appearance in the text
   */
  getCitations() {
    const annotations = this.getAnnotations();
    const messageContent = this.getDisplayContent();
    const citationMap = new Map();
    
    // First, collect all unique citations with their positions in the text
    annotations.forEach((annotation: any) => {
      if (annotation.type === 'url_citation' && annotation.urlCitation) {
        const key = annotation.urlCitation.url;
        if (!citationMap.has(key)) {
          // Find the position of this citation in the text
          const startIndex = typeof annotation.startIndex === 'number' 
            ? annotation.startIndex 
            : messageContent.indexOf(annotation.text);
          
          citationMap.set(key, {
            citation: annotation.urlCitation,
            position: startIndex >= 0 ? startIndex : Number.MAX_SAFE_INTEGER
          });
        }
      }
    });
    
    // Sort citations by their position in the text and return just the citation objects
    return Array.from(citationMap.values())
      .sort((a, b) => a.position - b.position)
      .map(item => item.citation);
  }

  /**
   * Update message content (for streaming)
   */
  updateContent(newContent: string): void {
    this.content += newContent;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Set final message content (for completion)
   */
  setFinalContent(content: string): void {
    this.content = content;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Mark message as completed (end streaming)
   */
  markAsCompleted(): void {
    this.status = 'sent';
  }

  /**
   * Create from API response
   */
  static fromApiResponse(data: any): ChatMessage {
    return new ChatMessage(data);
  }

  /**
   * Create user message
   */
  static createUserMessage(content: string, sessionId: string): ChatMessage {
    return new ChatMessage({
      content,
      sessionId,
      role: 'user'
    });
  }

  /**
   * Create user message with temporary ID for optimistic updates
   */
  static createUserMessageWithTempId(content: string, sessionId: string, tempId?: string): ChatMessage {
    const actualTempId = tempId || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return new ChatMessage({
      content,
      sessionId,
      role: 'user',
      tempId: actualTempId,
      id: actualTempId // Use temp ID as actual ID initially
    });
  }

  /**
   * Create assistant placeholder for streaming
   */
  static createAssistantPlaceholder(messageId: string, sessionId: string, metadata?: ChatMessageMetadata): ChatMessage {
    return new ChatMessage({
      id: messageId,
      content: '',
      sessionId,
      role: 'assistant',
      isStreaming: true,
      isPlaceholder: true,
      status: 'sending',
      metadata
    });
  }

  /**
   * Create assistant message
   */
  static createAssistantMessage(content: string, sessionId: string, streaming?: boolean): ChatMessage;
  static createAssistantMessage(messageId: string, content: string, sessionId: string): ChatMessage;
  static createAssistantMessage(
    contentOrId: string, 
    sessionIdOrContent: string, 
    streamingOrSessionId?: boolean | string
  ): ChatMessage {
    // Handle overloaded signatures
    if (typeof streamingOrSessionId === 'string') {
      // New signature: createAssistantMessage(messageId, content, sessionId)
      return new ChatMessage({
        id: contentOrId,
        content: sessionIdOrContent,
        sessionId: streamingOrSessionId,
        role: 'assistant',
        isStreaming: false,
        status: 'sent'
      });
    } else {
      // Legacy signature: createAssistantMessage(content, sessionId, streaming)
      const streaming = streamingOrSessionId || false;
      return new ChatMessage({
        content: contentOrId,
        sessionId: sessionIdOrContent,
        role: 'assistant',
        isStreaming: streaming,
        status: streaming ? 'sending' : 'sent'
      });
    }
  }

  /**
   * Create system message
   */
  static createSystemMessage(content: string, sessionId: string): ChatMessage {
    return new ChatMessage({
      content,
      sessionId,
      role: 'system'
    });
  }
}
